/**
 * NEOPINE motion — Apple-inspired scroll choreography.
 *
 * Design intent
 * -------------
 * The reference is Apple's product pages. There, motion is calm, slow and
 * sparse: every element travels only a few dozen pixels and settles on a long
 * ease-out curve. Nothing loops and nothing bounces.
 *
 * Where we deliberately differ: Apple plays each element once, for the whole
 * life of the page. Here a block plays again every time it comes back into
 * view, because the upward read carries as much weight as the downward one —
 * scrollback that lands on a page which has gone completely still feels dead.
 * See `REPLAY_ON_REENTRY` for the switch and for what it costs.
 *
 * How it is wired
 * ---------------
 * - `snippets/neopine-motion.liquid` runs inline in <head> and puts `.np-motion`
 *   on <html> before first paint — but only when IntersectionObserver exists and
 *   the visitor has not asked for reduced motion. Every "hidden" initial state
 *   in neopine-custom.css is gated behind that class, so a JS failure can never
 *   leave the page blank. That snippet also arms a safety net which drops the
 *   class again if this module never boots.
 * - This module tags elements with `data-np-reveal` (and a per-element
 *   `--np-delay`) just before observing them, then flips `data-np-in` when they
 *   come into view.
 * - Two observers run over the same set. The reveal observer trims 8% off the
 *   bottom of the root, so its "not intersecting" verdict arrives while a block
 *   is still visible in that strip. The exit observer is sized to the real
 *   viewport, and only its verdict is trusted to restore the hidden state.
 * - Only opacity and transform are animated, so nothing ever reflows and the
 *   cumulative layout shift stays at zero.
 *
 * @version 1.1.0
 */

/** Reveal marker set by this module; the CSS keys its hidden state off it. */
const REVEAL_ATTR = 'data-np-reveal';
/** Set when the element has entered; the CSS treats it as "settled". */
const IN_ATTR = 'data-np-in';
/** Mirrors the inline `--np-delay` for easier debugging in devtools. */
const DELAY_ATTR = 'data-np-delay';
/** Opt-out: a block carrying this plays once and is never reset. */
const ONCE_ATTR = 'data-np-once';

/**
 * Whether a block replays after it has left the viewport and come back.
 *
 * `true` — the default, and the behaviour the site is signed off on: scrolling
 * back up is as alive as scrolling down. A block is reset only once it has
 * cleared the viewport completely, so the reset itself is never visible.
 *
 * `false` — the strict Apple reading: every element enters once and the page is
 * then still for good. Cheaper, and quieter on very long pages.
 */
const REPLAY_ON_REENTRY = true;

/** Gap between siblings inside a content block. */
const STAGGER_MS = 85;
/** Hero text moves on a slightly wider beat so the load reads as a sequence. */
const HERO_STAGGER_MS = 115;
/** Stagger stops growing after this many siblings, so long grids stay snappy. */
const MAX_STAGGER_STEPS = 6;
/** How long to wait for new DOM before re-scanning (section rendering, morph). */
const RESCAN_DEBOUNCE_MS = 180;
/** How much the hero image pushes in across the whole of its exit. */
const HERO_SCALE_GAIN = 0.055;

/**
 * Never touch anything inside these. The header, footer, overlays and the
 * self-animating components all have their own timing and would fight ours.
 */
const EXCLUDE_SELECTOR = [
  '#header-group',
  '#footer-group',
  '.shopify-section-group-header-group',
  'dialog',
  '[role="dialog"]',
  '.cart-drawer',
  'search-modal',
  '.predictive-search',
  '.age-gate',
  '.marquee',
  '.slideshow',
  '.comparison-slider',
  '.quick-order-list',
  'sticky-add-to-cart',
  '[data-np-no-motion]',
].join(',');

/** Blocks that should animate on their own, rather than as part of a wrapper. */
const BLOCK_SELECTOR = '.image-block, .text-block, .group-block, .product-card, .collection-card';

/** Direct children of these containers are the real "rows" of a section. */
const ROW_SELECTOR = '[data-testid="section-content"] > *, .hero__content-wrapper .group-block > *';

/** Blocks whose inner image should settle in from a slightly larger crop. */
const MEDIA_SELECTOR = '.image-block, .group-block__media-wrapper, .hero__media';

/** A newly inserted node must match this before we bother re-scanning. */
const RESCAN_TRIGGER = `.section, .shopify-section, .hero, ${BLOCK_SELECTOR}`;

/** @type {IntersectionObserver | null} */
let observer = null;
/** @type {IntersectionObserver | null} */
let exitObserver = null;
/** @type {Element | null} */
let root = null;
/** @type {number | null} */
let rescanTimer = null;

/**
 * Whether the visitor has asked the OS to reduce motion.
 *
 * @returns {boolean}
 */
function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Resolves the element that wraps the page body copy.
 *
 * Re-resolved on every scan because the theme's view transitions can replace
 * the whole main element, which would leave a cached reference detached.
 *
 * @returns {Element}
 */
function getRoot() {
  return document.querySelector('#MainContent') ?? document.querySelector('main') ?? document.body;
}

/**
 * DOM depth, used to decide which of two nested candidates animates.
 *
 * @param {Element} element
 * @returns {number}
 */
function depthOf(element) {
  let depth = 0;
  let node = element;

  while ((node = node.parentElement)) depth += 1;

  return depth;
}

/**
 * Decides whether a candidate is still fair game.
 *
 * Runs during collection, and collection sets the attribute as it goes, so the
 * ancestor/descendant checks below can rely on `data-np-reveal` already being
 * present.
 *
 * @param {Element} element
 * @returns {boolean}
 */
function isEligible(element) {
  if (element.hasAttribute(REVEAL_ATTR)) return false;
  // An already-claimed ancestor means this element is moving as part of it.
  // The hero is the reason this matters: its <picture> and the <img> inside
  // share the `hero__media` class, so both would be claimed independently and
  // the image would animate twice, at two different rates.
  if (element.parentElement?.closest(`[${REVEAL_ATTR}]`)) return false;
  if (element.closest(EXCLUDE_SELECTOR)) return false;

  return true;
}

/**
 * Picks the reveal flavour for a block.
 *
 * @param {Element} element
 * @returns {'media' | 'up'}
 */
function variantFor(element) {
  return element.matches(MEDIA_SELECTOR) ? 'media' : 'up';
}

/**
 * Marks an element as a reveal target.
 *
 * The attribute is written immediately so sibling passes can see it.
 *
 * @param {Element} element
 * @param {number} step - Milliseconds between this element and its sibling.
 * @returns {{ element: Element, step: number }}
 */
function claim(element, step) {
  element.setAttribute(REVEAL_ATTR, variantFor(element));

  return { element, step };
}

/**
 * Finds every element on the page that should enter on scroll.
 *
 * Order matters. Leaf blocks are claimed before the rows and grids that contain
 * them, and a candidate is dropped as soon as it already has a claimed
 * descendant — that is what stops a section from double-animating as both a
 * container and its contents.
 *
 * @returns {Array<{ element: Element, step: number }>}
 */
function collect() {
  const items = [];

  // Hero copy first: this is the page-load choreography, not a scroll reveal.
  for (const wrapper of root.querySelectorAll('.hero__content-wrapper')) {
    const lines = wrapper.querySelectorAll('.group-block > *');
    const candidates = lines.length ? lines : wrapper.children;

    for (const element of candidates) {
      if (isEligible(element)) items.push(claim(element, HERO_STAGGER_MS));
    }
  }

  // Hero media: parades in behind the copy.
  for (const element of root.querySelectorAll('.hero__media')) {
    if (isEligible(element)) items.push(claim(element, 0));
  }

  // Repeating units — product and collection tiles cascade into view.
  for (const element of root.querySelectorAll('.product-card, .collection-card')) {
    if (isEligible(element)) items.push(claim(element, STAGGER_MS));
  }

  // Everything else, deepest first so an outer wrapper never steals the show
  // from the pieces inside it.
  const blocks = [...root.querySelectorAll(`${BLOCK_SELECTOR}, ${ROW_SELECTOR}`)];
  blocks.sort((a, b) => depthOf(b) - depthOf(a));

  for (const element of blocks) {
    if (!isEligible(element)) continue;
    if (element.querySelector(`[${REVEAL_ATTR}]`)) continue;
    if (element.parentElement?.closest(`[${REVEAL_ATTR}]`)) continue;

    items.push(claim(element, STAGGER_MS));
  }

  return items;
}

/**
 * Turns per-sibling position into a transition delay.
 *
 * Staggering is scoped to a shared parent, so a grid cascades but two unrelated
 * sections never inherit each other's timing. Long grids flatline at
 * MAX_STAGGER_STEPS so the last card is not left waiting.
 *
 * @param {Array<{ element: Element, step: number }>} items
 */
function assignDelays(items) {
  /** @type {Map<Element, Array<{ element: Element, step: number }>>} */
  const groups = new Map();

  for (const item of items) {
    const parent = item.element.parentElement ?? root;
    const group = groups.get(parent);

    if (group) group.push(item);
    else groups.set(parent, [item]);
  }

  for (const group of groups.values()) {
    group.sort((a, b) =>
      a.element.compareDocumentPosition(b.element) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1
    );

    group.forEach((item, index) => {
      const delay = Math.round(Math.min(index, MAX_STAGGER_STEPS) * item.step);

      item.element.style.setProperty('--np-delay', `${delay}ms`);
      item.element.setAttribute(DELAY_ATTR, String(delay));
    });
  }
}

/**
 * Starts watching a batch of elements.
 *
 * Anything already scrolled past when we get here — a restored scroll position,
 * an anchor jump, a back/forward cache restore — is revealed outright, because
 * an IntersectionObserver would never fire for it and the block would stay
 * invisible forever. With replay on, the exit observer immediately takes that
 * reveal back (the block is off screen, so the swap is invisible) and the block
 * is armed to play when the visitor scrolls back up to it.
 *
 * @param {Array<{ element: Element, step: number }>} items
 */
function observe(items) {
  for (const { element } of items) {
    if (element.getBoundingClientRect().bottom <= 0) {
      element.style.setProperty('--np-delay', '0ms');
      element.setAttribute(IN_ATTR, '');
    }

    observer?.observe(element);
    exitObserver?.observe(element);
  }
}

/**
 * Scans the page and arms every reveal it finds.
 */
function scan() {
  if (!observer) return;

  root = getRoot();

  const items = collect();

  if (!items.length) return;

  assignDelays(items);

  // Commit the hidden state to the layout before anything can be revealed,
  // otherwise the very first paint of the hero would skip its transition.
  void document.body.offsetHeight;

  requestAnimationFrame(() => requestAnimationFrame(() => observe(items)));
}

/**
 * Queues a scan for the next quiet moment.
 */
function scheduleScan() {
  if (rescanTimer !== null) return;

  rescanTimer = window.setTimeout(() => {
    rescanTimer = null;
    scan();
  }, RESCAN_DEBOUNCE_MS);
}

/**
 * Re-scans whenever the theme swaps content in — section rendering after a
 * filter or pagination change, or a view-transition navigation.
 */
function watchForNewContent() {
  const mutationObserver = new MutationObserver((records) => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (node.nodeType !== Node.ELEMENT_NODE) continue;

        const element = /** @type {Element} */ (node);

        if (element.matches(RESCAN_TRIGGER) || element.querySelector(RESCAN_TRIGGER)) {
          scheduleScan();

          return;
        }
      }
    }
  });

  mutationObserver.observe(document.body, { childList: true, subtree: true });
}

/**
 * Binds a slow push-in to the hero image as the hero leaves the viewport.
 *
 * This is deliberately a scale and not a translation. `.hero__media` clips its
 * overflow, so sliding the image would peel the background in along the leading
 * edge; growing it can only ever be cropped away. Scale is 1 at rest, so the
 * hero renders exactly as designed until the visitor actually scrolls.
 *
 * The value is written to a custom property rather than straight to
 * `transform`, keeping the effect on the compositor and off the layout path.
 * Reads are batched into one requestAnimationFrame per scroll burst, and the
 * loop short-circuits once the hero has left the viewport.
 */
function initHeroPushIn() {
  const targets = [...document.querySelectorAll('.hero__media img')];

  if (!targets.length) return;

  let ticking = false;

  const update = () => {
    ticking = false;

    const viewportHeight = window.innerHeight;

    for (const image of targets) {
      const host = image.closest('.hero__media-grid') ?? image.closest('.hero') ?? image;
      const rect = host.getBoundingClientRect();

      if (rect.bottom < 0 || rect.top > viewportHeight) continue;

      // 0 while the hero sits at the top of the page, 1 once it has scrolled
      // fully past.
      const progress = Math.min(1, Math.max(0, -rect.top / Math.max(1, rect.height)));

      image.style.setProperty('--np-hero-scale', (1 + progress * HERO_SCALE_GAIN).toFixed(4));
    }
  };

  const onScroll = () => {
    if (ticking) return;

    ticking = true;
    requestAnimationFrame(update);
  };

  update();

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });
}

/**
 * Boots the choreography. Bails out when the inline head script decided the
 * visitor should not get motion in the first place.
 */
function start() {
  if (!document.documentElement.classList.contains('np-motion')) return;
  if (prefersReducedMotion()) return;

  // The module is alive, so disarm the "reveal everything" safety net.
  if (window.__npMotionTimer) {
    clearTimeout(window.__npMotionTimer);
    window.__npMotionTimer = null;
  }

  observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;

        entry.target.setAttribute(IN_ATTR, '');

        // Without replay there is nothing left to wait for, so stop paying for
        // this element on every subsequent scroll frame.
        if (!REPLAY_ON_REENTRY) observer?.unobserve(entry.target);
      }
    },
    // Hold the trigger line just above the fold so a block has already settled
    // by the time the visitor's eye reaches it.
    { rootMargin: '0px 0px -8% 0px', threshold: 0 }
  );

  if (REPLAY_ON_REENTRY) {
    // Sized to the true viewport, unlike the reveal observer above. Its silence
    // is the only signal trusted to restore a block's hidden state: by the time
    // this observer reports "not intersecting", the block has cleared the
    // viewport entirely and resetting it cannot be seen — whereas the reveal
    // observer, whose root stops 8% short of the bottom, would call it out
    // while the block is still visible in that strip.
    exitObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) continue;
          if (entry.target.hasAttribute(ONCE_ATTR)) continue;

          entry.target.removeAttribute(IN_ATTR);
        }
      },
      { rootMargin: '0px', threshold: 0 }
    );
  }

  root = getRoot();
  scan();
  initHeroPushIn();
  watchForNewContent();

  window.NeopineMotion = { scan };
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start, { once: true });
} else {
  start();
}
